import { Component, inject, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  DeviceHistoryDevicePayloadDto,
  DeviceHistoryDto,
  DeviceHistoryEventDto,
  LicenseRedemptionPayloadDto,
  SoftwareSelectionResolutionDto,
} from 'src/app/models/device.model';
import { humanizeEnumValue } from 'src/app/utils/enum-display';
import { isSoftwareSelectionResolutionError } from 'src/app/utils/software-selection';
import {
  formatRelativeTimestamp,
  formatTimestamp,
} from 'src/utils/general-utils';

const EVENT_TYPE_TRANSLATION_KEYS: Readonly<Record<string, string>> = {
  DEVICE_AUTHORIZATION: 'device_history.event_type.device_authorization',
  DEVICE_CERTIFICATE_REGISTRATION:
    'device_history.event_type.device_certificate_registration',
  DEVICE_CERTIFICATE_REVOCATION:
    'device_history.event_type.device_certificate_revocation',
  LICENSE_REDEMPTION: 'device_history.event_type.license_redemption',
};

const REQUEST_TYPE_TRANSLATION_KEYS: Readonly<Record<string, string>> = {
  GET_ALL_SOFTWARE: 'device_history.request_type.get_all_software',
  INDIVIDUAL_SOFTWARE: 'device_history.request_type.individual_software',
};

@Component({
  selector: 'app-device-history',
  templateUrl: './device-history.component.html',
  styleUrl: './device-history.component.scss',
  standalone: false,
})
export class DeviceHistoryComponent {
  private readonly translate = inject(TranslateService);

  @Input() history?: DeviceHistoryDto | null;
  @Input() hasLicenses?: boolean;

  get events(): DeviceHistoryEventDto[] {
    return this.history?.events ?? [];
  }

  get latestSoftwareRequest():
    | DeviceHistoryEventDto<LicenseRedemptionPayloadDto>
    | undefined {
    return this.history?.latestGetAllSoftware;
  }

  get latestSoftwareComponents(): SoftwareSelectionResolutionDto[] {
    return this.latestSoftwareRequest?.payload?.components ?? [];
  }

  get latestSoftwareRequestType(): string | undefined {
    return this.latestSoftwareRequest?.payload?.requestType;
  }

  get latestSoftwareRequestHasErrors(): boolean {
    return (
      !!this.latestSoftwareRequest?.error ||
      this.latestSoftwareComponents.some(isSoftwareSelectionResolutionError)
    );
  }

  get showLatestSoftwareSection(): boolean {
    return this.hasLicenses !== false || !!this.latestSoftwareRequest;
  }

  get creatorName(): string | undefined {
    return this.displayName(this.history?.creator);
  }

  /** The masteradmin who was impersonating the creator, when the device was created that way. */
  get creatorImpersonatorName(): string | undefined {
    return this.displayName(this.history?.creator?.impersonatedBy);
  }

  private displayName(profile?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  }): string | undefined {
    const fullName = [profile?.firstName, profile?.lastName]
      .filter((part): part is string => !!part?.trim())
      .join(' ');

    return fullName || profile?.email?.trim() || undefined;
  }

  get hasCreationInfo(): boolean {
    return !!(this.history?.createdAt || this.creatorName);
  }

  formatTime(timestamp?: string): string {
    return formatTimestamp(timestamp);
  }

  formatTimeAgo(timestamp?: string): string {
    const locale =
      this.translate.currentLang || this.translate.getDefaultLang() || 'en';
    return formatRelativeTimestamp(timestamp, locale);
  }

  formatEventType(eventType?: string): string {
    return this.translateKnownValue(
      eventType,
      EVENT_TYPE_TRANSLATION_KEYS,
      'device_history.events.unknown',
    );
  }

  formatRequestType(requestType?: string): string {
    return this.translateKnownValue(requestType, REQUEST_TYPE_TRANSLATION_KEYS);
  }

  getEventDevicePayload(
    event: DeviceHistoryEventDto,
  ): DeviceHistoryDevicePayloadDto | undefined {
    const payload = event.payload;
    return payload && !this.isLicenseRedemptionPayload(payload)
      ? payload
      : undefined;
  }

  getEventComponentCount(event: DeviceHistoryEventDto): number | undefined {
    const payload = event.payload;
    return this.isLicenseRedemptionPayload(payload)
      ? (payload.components?.length ?? 0)
      : undefined;
  }

  trackEvent(index: number, event: DeviceHistoryEventDto): string {
    return `${event.time ?? 'no-time'}:${event.eventType ?? 'unknown'}:${index}`;
  }

  private isLicenseRedemptionPayload(
    payload: DeviceHistoryEventDto['payload'],
  ): payload is LicenseRedemptionPayloadDto {
    return payload?.type === 'LicenseRedemption';
  }

  private translateKnownValue(
    value: string | undefined,
    translationKeys: Readonly<Record<string, string>>,
    emptyTranslationKey?: string,
  ): string {
    if (!value) {
      return emptyTranslationKey
        ? this.translate.instant(emptyTranslationKey)
        : '';
    }

    const translationKey = translationKeys[value];
    return translationKey
      ? this.translate.instant(translationKey)
      : humanizeEnumValue(value);
  }
}
